import React, { useContext, useEffect, useRef, useState } from "react";
import SearchBar from "../../components/common/SearchBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CircularProgress } from "@mui/material";
import MapFilterMenu from "./common/MapFilterMenu";
import CustomButton from "../../components/common/Button";
import useShipmentList from "./hooks/useShipmentList";
import SelectedShipmentsList from "./components/SelectedShipmentsList";
import AllShipmentsList from "./components/AllShipmentsList";
import { defaultShipmentMapFilters } from "../../constants/filters";
import FiltersList from "../../components/FiltersList";
import DistanceFilter from "../../components/DistanceFilter"
import DiscreteSliderLabel from "./components/SliderFilter";
import { orderAgeMarks, shipmentHubStatuses, shipmentPaymentMethod } from "constants/constants";
import { orderStatusTypes, shipmentTypes } from "constants/enums";
import MapIsTypingContext from "context/MapIsTypingContext";
import { ArrowUpDuotoneIcon, ArrowDownDuotoneIcon, ResetIcon, OnlyScheduledOrdersDuotoneIcon } from "components/svg";
import { useLazyQuery } from "@apollo/client";
import { stagingOptions } from "../../constants/apollo";
import { GET_ALL_HUBS_FOR_FILTER, GET_Merchants_BY_IDS } from "../../api/queries/FilterQueries";
import { days, daysReverse } from "constants/map";
import { MAP_ORDER_FETCH_USE_CUSTOM_DATE } from "../../helper/getFetchFilters";
import DatePickerDDMMYY from "../../components/common/DatePickerDDMMYY";

function ShipmentsList({
  handleQueryChange,
  shipmentCursor,
  hasNextPage,
  fetch_shipments,
  fetch_hubs,
  fetch_merchants,
  fetch_merchant,
  setShipmentFilters,
  shipmentFilters,
  setSelectedShipmentIds,
  selectedShipmentIds,
  selectedShipments,
  shipments,
  hubs,
  reloadingShipment,
  mapBounds,
  searchText,
  setSearchText,
  loading,
  setFilterUsed,
  shipmentListLoading,
  shipmentLoadingText,
  isFilterOpen,
  onlyScheduledOrders,
  setOnlyScheduledOrders,
}) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [shipmentFilterApplied, setShipmentFilterApplied] = useState(false);
  const [getHubs] = useLazyQuery(GET_ALL_HUBS_FOR_FILTER, stagingOptions);
  const [getMerchantsByIds] = useLazyQuery(GET_Merchants_BY_IDS, stagingOptions);
  const {
    t,
    selectedRef,
    filterAreaOpen,
    setFilterAreaOpen,
    pinnedFilterIds,
    setPinnedFilterIds,
    handleShipmentClick,
    handleDeselectAllItems,
    limitMessage,
    setLimitMessage,
  } = useShipmentList({
    reloadingShipment,
    setSelectedShipmentIds,
    selectedShipmentIds,
    searchText,
    setSearchText,
  });

  const topDiv = useRef(null);
  const bottomDiv = useRef(null);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [orderDateByCustomRange, setOrderDateByCustomRange] = useState(() =>
    Boolean(shipmentFilters?.shipmentDateFrom || shipmentFilters?.shipmentDateTo)
  );
  const { setIsInputFocused } = useContext(MapIsTypingContext);

  const mapOrderFetchFilters = () => ({
    ...shipmentFilters,
    [MAP_ORDER_FETCH_USE_CUSTOM_DATE]: orderDateByCustomRange,
  });

  const dateModeSwitchBadgeClassName =
    "inline-flex items-center justify-center shrink-0 min-h-[22px] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide leading-none rounded-full border border-primaryColor/25 bg-primaryColor/[0.08] text-primaryColor hover:bg-primaryColor/15 hover:border-primaryColor/40 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor/35 transition-colors";

  useEffect(() => {
    if (limitMessage) {
      toast(limitMessage, {
        type: "error",
      });
      setLimitMessage("");
    }
  }, [limitMessage]);

  useEffect(() => {
    setFetchMoreLoading(false);
  }, [shipments]);

  useEffect(() => {
    try {
      const persisted = JSON.parse(localStorage?.getItem("pinnedFilterIds") || "[]");
      setPinnedFilterIds(Array.isArray(persisted) ? persisted : []);
    } catch {
      setPinnedFilterIds([]);
    }
    if(isFilterOpen){
      setFilterAreaOpen(true)
    }
  }, []);

  useEffect(() => {
    setIsInputFocused(filterOpen);
  }, [filterOpen]);

  useEffect(() => {
    localStorage?.setItem("pinnedFilterIds", JSON.stringify(pinnedFilterIds || []));
  }, [pinnedFilterIds]);

  function handleFilter() {
    handleQueryChange(shipmentFilters);
    fetch_shipments({
      filters: mapOrderFetchFilters(),
      isShipmentFilterModalActive: true,
    });
    setFilterUsed(true);
    setShipmentFilterApplied(true);
  }

  async function handleFilterReset() {
    setOrderDateByCustomRange(false);
    handleQueryChange(defaultShipmentMapFilters);
    setShipmentFilters((old) => ({ ...old, ...defaultShipmentMapFilters }));
    fetch_shipments({
      filters: { ...defaultShipmentMapFilters, [MAP_ORDER_FETCH_USE_CUSTOM_DATE]: false },
    });
    setFilterAreaOpen(false);
    setFilterOpen(false);
    setFilterUsed(true);
    setShipmentFilterApplied(false);
  }

  async function fetchOnlyScheduledOrders() {
    setOnlyScheduledOrders((pre) => !pre);
  }

  useEffect(() => {
    handleQueryChange({ onlyScheduledOrders: onlyScheduledOrders });
    setShipmentFilters((old) => ({ ...old, ...{ onlyScheduledOrders: onlyScheduledOrders } }));
  }, [onlyScheduledOrders]);

  const handleScroll = () => {
    topDiv.current.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  };
  const handleScrollDown = () => {
    setFilterAreaOpen(false);
    bottomDiv.current.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (filterAreaOpen) {
      handleScroll();
    }
  }, [filterAreaOpen]);

  useEffect(() => {
    if (shipmentFilters && mapBounds) {
      handleFilter();
    }
  }, [shipmentFilters, mapBounds, orderDateByCustomRange]);

  return (
    <>
      <div style={{ height: "98vh" }}>
        <div className="flex justify-between items-center space-x-3 relative mb-4 pr-1">
          <div className="flex space-x-3 ">
            <div className={`flex items-center gap-2`}>
              <h4 className="pt-2"
                style={{
                  color: "#3c4b64",
                  fontWeight: 450,
                  lineHeight: 1.2,
                  fontSize: "1.4125rem"
                }}>Orders
              </h4>
              {shipments?.length > 0 ? <span className="text-orange-500 bg-white px-1 rounded-md shadow-sm mt-2" style={{
                fontWeight: 450,
                fontSize: "12px"
              }}>{shipments?.length}</span> : <span></span>}
              {shipmentListLoading &&
                <span className="text-orange-500 bg-white px-1 rounded-md shadow-sm mt-2 p-1">
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                  <div className="loading-dot"></div>
                </span>}
            </div>
          </div>
          <div className="flex justify-center items-center">
            <div
              onClick={fetchOnlyScheduledOrders}
              className="pt-1 pr-2">
              <OnlyScheduledOrdersDuotoneIcon fill={onlyScheduledOrders ? "#dd9802ff" : "#666"} />
            </div>
            {filterAreaOpen && (<div
              onClick={handleFilterReset}
              className="pt-1 pr-1">
              <ResetIcon />
            </div>)}
            <MapFilterMenu open={filterAreaOpen} setOpen={setFilterAreaOpen} />
            <SearchBar
              value={searchText}
              onChange={setSearchText}
              placeholder={t("search_shipments")}
            />
            <div className="flex flex-col items-center justify-center pt-1 px-2">
              <div onClick={handleScroll} className="flex justify-center">
                <div className="bg-success rounded-md cursor-pointer">
                  <ArrowUpDuotoneIcon />
                </div>
              </div>
              <div onClick={handleScrollDown} className="flex justify-center">
                <div className="bg-success rounded-md cursor-pointer">
                  <ArrowDownDuotoneIcon />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={`flex flex-col h-[calc(100vh-83px)] overflow-y-scroll scrollbar-custom-widget`}>
          <span ref={topDiv}></span>
          {filterAreaOpen && (
            <div className="pb-3 mr-1">
              <div className="mb-2">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3 pl-[5px] pr-0.5">
                  <span className="text-base font-medium text-[#3c4b64] tracking-tight">
                    {!orderDateByCustomRange ? "Age of Order" : "Date range"}
                  </span>
                  {!orderDateByCustomRange ? (
                    <button
                      type="button"
                      className={dateModeSwitchBadgeClassName}
                      onClick={() => setOrderDateByCustomRange(true)}
                    >
                      Custom dates
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={dateModeSwitchBadgeClassName}
                      onClick={() => setOrderDateByCustomRange(false)}
                    >
                      Order age
                    </button>
                  )}
                </div>
                <div>
                  {!orderDateByCustomRange ? (
                    <div className="flex justify-center items-center w-full overflow-x-auto">
                      <DiscreteSliderLabel
                        min={1}
                        max={9}
                        marks={orderAgeMarks}
                        defaultVal={daysReverse[+shipmentFilters["shipmentReadySince"]]}
                        handleChange={(day) => {
                          setShipmentFilters((old) => ({
                            ...old,
                            shipmentReadySince: days[day],
                          }));
                        }}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-3">
                      <DatePickerDDMMYY
                        label="From"
                        value={shipmentFilters.shipmentDateFrom || ""}
                        onChange={(v) =>
                          setShipmentFilters((old) => ({ ...old, shipmentDateFrom: v }))
                        }
                        size="small"
                        fullWidth
                      />
                      <DatePickerDDMMYY
                        label="To"
                        value={shipmentFilters.shipmentDateTo || ""}
                        onChange={(v) =>
                          setShipmentFilters((old) => ({ ...old, shipmentDateTo: v }))
                        }
                        minDate={shipmentFilters.shipmentDateFrom || undefined}
                        size="small"
                        fullWidth
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="bg-white shadow-sm rounded-md pt-1">
                <FiltersList
                  selectedFilters={shipmentFilters}
                  filters={[
                    {
                      name: "Filter by Status",
                      value: "shipmentOrderStatus",
                      id: 2,
                      options: orderStatusTypes,
                      isMulti: true,
                    },
                    {
                      name: "Filter by Merchant",
                      value: "shipmentMerchant",
                      id: 3,
                      isAsync: true,
                      isSearchable: true,
                      isMulti: true,
                      getSelectedVal: async (ids) => {
                        if (ids && ids !== "all") {
                          const filteredIds = ids
                            .filter((id) => id !== "all")
                            .map(Number);
                          if (filteredIds.length > 0) {
                            const response = await getMerchantsByIds({
                              variables: { merchantIds: filteredIds },
                            });
                            return response?.data?.fetchMerchantsByIds ?? [];
                          }
                        }
                        return [{ value: "all", label: "All" }];
                      },
                      loadMore: (searchQuery, page) =>
                        fetch_merchants({ page: page, searchQuery: searchQuery }),
                    },
                    {
                      name: "Filter by Hub",
                      value: "hubIds",
                      id: 4,
                      isAsync: true,
                      isSearchable: true,
                      getSelectedVal: async (ids) => {
                        if (ids && ids != "all") {
                          const filteredIds = ids.filter(id => id !== "all").map(Number);
                          if (filteredIds.length) {
                            const response = await getHubs({ variables: { ids: filteredIds } });
                            return response?.data?.fetchAllHubs || { value: "all", label: "All" };
                          }
                        } else return { value: "all", label: "All" };
                      },
                      loadMore: (searchQuery, page) =>
                        fetch_hubs({
                          page,
                          searchQuery,
                          merchantId: shipmentFilters.shipmentMerchant,
                        }),
                      cacheUniqs: [shipmentFilters.shipmentMerchant],
                      isMulti: true,
                    },
                    {
                      name: "Filter by Shipment Type",
                      value: "shipmentType",
                      id: 5,
                      options: shipmentTypes,
                      isMulti: true,
                    },
                    {
                      name: "Filter by Hub Status",
                      value: "shipmentHubOpen",
                      id: 6,
                      options: shipmentHubStatuses,
                    },
                    {
                     name: "Filter by Payment Method",
                      value: "shipmentPaymentMethod",
                      id: 7,
                      options: shipmentPaymentMethod,
                      isMulti: true,
                    },
                  ]}
                  handleChange={(filterVal, filterName) => {
                    setShipmentFilters((old) => ({
                      ...old,
                      [filterName]: filterVal,
                    }));
                  }}
                />
                {/* <div className="flex gap-4 px-3 pb-3">
                  <div
                    onClick={handleFilterReset}
                    className="cursor-pointer gap-2 uppercase text-white font-bold bg-blue-700 hover:bg-blue-800 px-3 py-2 text-xs rounded-md w-full flex items-center justify-center">
                    <ResetIcon />
                    <span>Reset Filter</span>
                  </div>
                  <div
                    onClick={handleFilter}
                    className="cursor-pointer gap-2 uppercase text-white font-bold bg-green-700 hover:bg-green-800 px-3 py-2 text-xs rounded-md ">
                    Search
                  </div>
                </div> */}
              </div>
              <DistanceFilter setShipmentFilters={setShipmentFilters} shipmentFilters={shipmentFilters} />
            </div>
          )}
          <SelectedShipmentsList
            handleShipmentClick={handleShipmentClick}
            data={selectedShipments}
            selectedRef={selectedRef}
            selectedShipmentIds={selectedShipmentIds}
            handleDeselectAllItems={handleDeselectAllItems}
            selectedShipments={selectedShipments}
          />
          {!loading && (
            <AllShipmentsList
              data={shipments}
              selectedRef={selectedRef}
              handleShipmentClick={handleShipmentClick}
              selectedShipmentIds={selectedShipmentIds}
              selectedShipments={selectedShipments}
            >
              {shipments?.length > 0 && hasNextPage && (
                <div className="w-full flex items-center justify-center">
                  <CustomButton
                    onClick={() => {
                      if (!hasNextPage) return;
                      setFetchMoreLoading(true);
                      fetch_shipments({
                        cursor: shipmentCursor,
                        append: true,
                        load: false,
                        filters: mapOrderFetchFilters(),
                      })?.finally(() => setFetchMoreLoading(false));
                    }}
                  >
                    {fetchMoreLoading ? "Loading..." : "Load More"}
                  </CustomButton>
                </div>
              )}
            </AllShipmentsList>
          )}
          {loading && (
            <div>{[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="select-none max-w-[383px] rounded-xl bg-white hover:bg-lightGray text-xs hover:cursor-pointer transition-all mb-3 shadow-sm mr-1 line loading-shimmer"
              ></div>
            ))}
            </div>
          )}
          {shipments?.length == 0 && !loading && (
            <div className="mx-auto mt-10  flex flex-col items-center justify-center">
              {shipmentLoadingText}
            </div>
          )}
          <span className="p-2"></span>
          <span ref={bottomDiv}></span>
        </div>
      </div>
    </>
  );
}

export default React.memo(ShipmentsList);
